"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader, CircleCheck } from "lucide-react";
const ValuationRequest = () => {
  return <section id="valuation-request" className="bg-primary py-24 text-primary-foreground">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-4 text-white">
            Request Your Expert Valuation
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto font-sans">
            Get a precise, no-obligation rental valuation from our local property experts.
          </p>
        </div>

        <div className="bg-white/5 p-8 md:p-12 rounded-lg border border-white/10 backdrop-blur-sm">
          <FormProvider formId="valuation_form_landlords" sectionName="valuation-request" defaultValues={{
          landlord_name: "",
          email: "",
          phone: "",
          property_address: "",
          property_type: "",
          preferred_valuation_date: "",
          additional_details: ""
        }} validationRules={{
          landlord_name: {
            required: "Name is required"
          },
          email: {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          },
          phone: {
            required: "Phone number is required"
          },
          property_address: {
            required: "Property address is required"
          },
          property_type: {
            required: "Property type is required"
          }
        }} submitText="Book Free Valuation" successMessage="Thank you! Your valuation request has been received. We will contact you shortly.">
            <ValuationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
};
function ValuationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="bg-green-500/20 p-4 rounded-full">
          <CircleCheck className="w-16 h-16 text-green-400" />
        </div>
        <h3 className="text-2xl font-serif text-white">Request Received</h3>
        <p className="text-white/80 max-w-md">{successMessage}</p>
      </div>;
  }
  const inputClasses = "bg-transparent border-white/40 text-white placeholder:text-white/50 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 h-12";
  const labelClasses = "text-white/90 font-sans tracking-wide";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField name="landlord_name" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        {}
        <FormField name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        {}
        <FormField name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 7123 456789" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        {}
        <FormField name="property_type" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Property Type</FormLabel>
              <FormControl>
                <select {...field} className={`flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses} [&>option]:bg-primary [&>option]:text-white`}>
                  <option value="" disabled>
                    Select property type
                  </option>
                  {["Flat / Apartment", "Terraced House", "Semi-Detached House", "Detached House", "Studio", "Other"].map(opt => <option key={opt} value={opt}>
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        {}
        <div className="md:col-span-2">
          <FormField name="property_address" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Property Address</FormLabel>
                <FormControl>
                  <textarea {...field} placeholder="123 High Street, London, N1 2AB" className={`flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`} />
                </FormControl>
                <FormMessage className="text-red-300" />
              </FormItem>} />
        </div>

        {}
        <FormField name="preferred_valuation_date" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Preferred Valuation Date (Optional)</FormLabel>
              <FormControl>
                <Input type="date" {...field} className={`${inputClasses} [color-scheme:dark]`} />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        {}
        <div className="md:col-span-2">
          <FormField name="additional_details" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClasses}>Additional Details (Optional)</FormLabel>
                <FormControl>
                  <textarea {...field} placeholder="Any special features, recent renovations, etc." className={`flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`} />
                </FormControl>
                <FormMessage className="text-red-300" />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] h-14 text-lg bg-white text-black hover:bg-gray-200 border-none font-semibold tracking-wide">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : "Book Free Valuation"}
        </Button>
      </div>
    </Form>;
}
export default ValuationRequest;